import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import CoachingPage from './components/CoachingPage';
import SessionDetail from './components/SessionDetail';
import AuthModal from './components/AuthModal';
import OAuthCallback from './components/OAuthCallback';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import Calendar from './components/Calendar';
import MySpeeches from './components/MySpeeches';
import BrowseSpeeches from './components/BrowseSpeeches';
import ClubSpeechDetail from './components/ClubSpeechDetail';
import MeetingDetail from './components/MeetingDetail';
import AdminPanel from './components/AdminPanel';
import JoinPage from './components/JoinPage';
import CheckinPage from './components/CheckinPage';
import Leaderboard from './components/Leaderboard';
import ProgressPage from './components/ProgressPage';
import PeoplePage from './components/PeoplePage';
import FriendRequestsBell from './components/FriendRequestsBell';
import JoinRequestScreen from './components/JoinRequestScreen';
import OnboardingModal from './components/OnboardingModal';
import ToastContainer from './components/ToastContainer';
import AppFeedbackButton from './components/AppFeedbackButton';
import Larry from './components/Larry';
import useAuth from './hooks/useAuth';
import useAuthStore from './store/authStore';
import useTheme from './hooks/useTheme';
import { getCurrentMember } from './services/api';
import { FEATURES } from './config/features';

// Filtered at the definition site so both nav renderers (desktop + mobile
// hamburger) pick the change up without touching their JSX.
const NAV_ITEMS = [
  { to: '/', label: 'Home', end: true },
  { to: '/calendar', label: 'Schedule', enabled: FEATURES.SCHEDULING },
  { to: '/my-speeches', label: 'My Speeches' },
  { to: '/browse', label: 'Library' },
  { to: '/people', label: 'People' },
  { to: '/leaderboard', label: 'Leaderboard', enabled: FEATURES.GAMIFICATION },
  { to: '/analyze', label: 'Coaching', enabled: FEATURES.COACHING },
].filter(i => i.enabled !== false);

const MEMBER_NAV_ITEMS = [
  { to: '/calendar', label: 'Schedule', enabled: FEATURES.SCHEDULING },
  { to: '/my-speeches', label: 'My Speeches' },
  { to: '/browse', label: 'Library' },
  { to: '/people', label: 'People' },
  { to: '/leaderboard', label: 'Leaderboard', enabled: FEATURES.GAMIFICATION },
  { to: '/analyze', label: 'Coaching', enabled: FEATURES.COACHING },
].filter(i => i.enabled !== false);

// Element used for routes belonging to a hidden module. The <Route> entries stay
// in the table (rather than being deleted) for two reasons: there is no path="*"
// catch-all, so a missing route renders a blank screen instead of redirecting;
// and keeping the entries keeps their components referenced, so CI=true builds
// don't fail on no-unused-vars. Members land back on home.
const HIDDEN_ROUTE = <Navigate to="/" replace />;

function Header({ user, isAuthenticated, member, onLoginClick, onLogoutClick, theme, toggleTheme }) {
  const isAdmin = member?.role === 'admin';
  const isMemberOnly = member?.role === 'member';
  const activeNavItems = isMemberOnly ? MEMBER_NAV_ITEMS : NAV_ITEMS;
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMobileOpen(false); }, [location]);

  const navLinkClass = ({ isActive }) =>
    clsx(
      'pill-tab text-sm',
      isActive ? 'active' : ''
    );

  return (
    <header className="sticky top-0 z-50 px-4 py-3" style={{ background: 'var(--bg-header)', backdropFilter: 'blur(20px)', borderBottom: '1px solid var(--border-subtle)' }}>
      <div className="max-w-5xl mx-auto flex items-center justify-between">
        {/* Wordmark */}
        <NavLink to="/" className="flex items-center gap-2.5 group">
          {/* Waveform glyph */}
          <div className="flex items-center gap-[3px]" style={{ height: 16 }}>
            {['animate-wave-breathe-1','animate-wave-breathe-2','animate-wave-breathe-3','animate-wave-breathe-4','animate-wave-breathe-5'].map((cls, i) => (
              <div key={i} className={`rounded-full transition-opacity group-hover:opacity-100 ${cls}`} style={{ width: 2.5, height: `${[0.5,0.75,1,0.75,0.5][i] * 16}px`, background: 'var(--accent)', opacity: 0.75, transformOrigin: 'center' }} />
            ))}
          </div>
          <div>
            <h1 className="font-display text-[17px] font-semibold leading-tight tracking-tight" style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
              Larynx
            </h1>
            <p className="text-xs font-medium hidden sm:block uppercase tracking-[0.25em]" style={{ color: 'var(--text-faint)' }}>
              Speech Club
            </p>
          </div>
        </NavLink>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {activeNavItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className={navLinkClass}>
              {item.label}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink to="/admin" className={navLinkClass}>
              Admin
            </NavLink>
          )}
        </nav>

        {/* Right side: theme toggle + user + mobile toggle */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Theme toggle — beta */}
          {toggleTheme && (
            <button
              onClick={toggleTheme}
              title={theme === 'light' ? 'Switch to Dark' : 'Switch to Light'}
              aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              style={{
                padding: '5px 8px',
                borderRadius: 8,
                border: '1px solid var(--border-accent)',
                background: 'var(--accent-dim)',
                color: 'var(--text-muted)',
                fontSize: 11,
                fontWeight: 600,
                fontFamily: "'Outfit', sans-serif",
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                lineHeight: 1.4,
              }}
            >
              <span style={{ fontSize: 14 }}>{theme === 'light' ? '☀︎' : '☾'}</span>
            </button>
          )}

          {isAuthenticated && <FriendRequestsBell />}

          {isAuthenticated ? (
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline text-sm font-bold" style={{ color: 'var(--text-secondary)' }}>
                {user?.firstName}
              </span>
              <button
                onClick={onLogoutClick}
                className="pill-tab"
                style={{ color: 'var(--text-muted)' }}
              >
                Logout
              </button>
            </div>
          ) : (
            <button
              onClick={onLoginClick}
              className="btn-bouncy text-sm px-5 py-2"
            >
              Sign In
            </button>
          )}

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 rounded-card hover:opacity-70 transition-opacity"
            style={{ color: 'var(--text-secondary)' }}
            aria-label="Toggle navigation"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              {mobileOpen ? (
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              ) : (
                <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Nav Dropdown */}
      {mobileOpen && (
        <nav className="md:hidden mt-3 flex flex-wrap gap-2 animate-slide-up pb-2">
          {activeNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={navLinkClass}
              onClick={() => setMobileOpen(false)}
            >
              {item.label}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink to="/admin" className={navLinkClass} onClick={() => setMobileOpen(false)}>
              Admin
            </NavLink>
          )}
        </nav>
      )}
    </header>
  );
}


function PageShell({ title, subtitle, children, headerProps, wide = false }) {
  return (
    <div className="min-h-screen bg-canvas">
      <Header {...headerProps} />
      <main className={clsx('mx-auto px-4 py-8 pb-24 md:pb-8', wide ? 'max-w-5xl' : 'max-w-3xl')}>
        {(title || subtitle) && (
          <div className="mb-6">
            {title && (
              <div className="flex items-center gap-3">
                <div className="w-0.5 h-7 rounded-full flex-shrink-0" style={{ background: 'linear-gradient(180deg, var(--accent) 0%, transparent 100%)' }} />
                <h2 className="font-display text-2xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>{title}</h2>
              </div>
            )}
            {subtitle && <p className="mt-1.5 text-sm font-medium pl-3.5" style={{ color: 'var(--text-muted)' }}>{subtitle}</p>}
          </div>
        )}
        {children}
      </main>
    </div>
  );
}

function AppContent({ headerProps }) {
  const { member } = headerProps;
  const isAdmin = member?.role === 'admin';
  const isMemberOnly = member?.role === 'member';

  const navigate = useNavigate();

  const [showOnboarding, setShowOnboarding] = useState(
    () => member?.role !== 'admin' && !localStorage.getItem('larynx_onboarded')
  );

  const dismissOnboarding = (navigateTo) => {
    localStorage.setItem('larynx_onboarded', '1');
    setShowOnboarding(false);
    if (navigateTo) navigate(navigateTo);
  };

  return (
    <>
    {showOnboarding && member && (
      <OnboardingModal member={member} onDismiss={dismissOnboarding} />
    )}
    <Routes>
      {/* Join Page & Check-in - accessible to all authenticated users */}
      <Route path="/join" element={<JoinPage />} />
      <Route path="/checkin" element={FEATURES.GAMIFICATION ? <CheckinPage /> : HIDDEN_ROUTE} />

      {/* Speech Club Routes */}
      <Route
        path="/"
        element={
          isMemberOnly ? (
            <Navigate to="/my-speeches" replace />
          ) : (
            <PageShell headerProps={headerProps} wide>
              <Dashboard />
            </PageShell>
          )
        }
      />
      <Route path="/schedule" element={FEATURES.SCHEDULING ? <Navigate to="/calendar" replace /> : HIDDEN_ROUTE} />
      <Route
        path="/calendar"
        element={
          FEATURES.SCHEDULING ? (
            <PageShell title="Schedule" headerProps={headerProps} wide>
              <Calendar />
            </PageShell>
          ) : HIDDEN_ROUTE
        }
      />
      <Route
        path="/people"
        element={
          <PageShell title="People" subtitle="Find members and swap videos." headerProps={headerProps} wide>
            <PeoplePage />
          </PageShell>
        }
      />
      <Route
        path="/my-speeches"
        element={
          <PageShell title="My Speeches" headerProps={headerProps} wide>
            <MySpeeches />
          </PageShell>
        }
      />
      <Route
        path="/browse"
        element={
          <PageShell title="Library" headerProps={headerProps} wide>
            <BrowseSpeeches />
          </PageShell>
        }
      />
      <Route
        path="/meetings/:meetingId"
        element={
          FEATURES.SCHEDULING ? (
            <PageShell headerProps={headerProps} wide>
              <MeetingDetail />
            </PageShell>
          ) : HIDDEN_ROUTE
        }
      />
      <Route
        path="/speeches/:speechId"
        element={
          <PageShell headerProps={headerProps} wide>
            <ClubSpeechDetail />
          </PageShell>
        }
      />
      <Route
        path="/leaderboard"
        element={
          FEATURES.GAMIFICATION ? (
            <PageShell headerProps={headerProps} wide>
              <Leaderboard />
            </PageShell>
          ) : HIDDEN_ROUTE
        }
      />
      <Route
        path="/progress"
        element={
          <PageShell title="My Progress" subtitle="Every speech is data. Here's yours." headerProps={headerProps} wide>
            <ProgressPage />
          </PageShell>
        }
      />
      <Route
        path="/admin"
        element={
          isAdmin ? (
            <PageShell title="Admin Panel" headerProps={headerProps} wide>
              <AdminPanel />
            </PageShell>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />

      {/* Coaching — unified analyze + history surface */}
      <Route
        path="/analyze"
        element={
          FEATURES.COACHING ? (
            <PageShell
              title="Coaching"
              subtitle="Analyze a speech and revisit your history."
              headerProps={headerProps}
              wide
            >
              <CoachingPage />
            </PageShell>
          ) : HIDDEN_ROUTE
        }
      />
      {/* Former history page — folded into Coaching */}
      <Route path="/history" element={FEATURES.COACHING ? <Navigate to="/analyze" replace /> : HIDDEN_ROUTE} />
      <Route
        path="/history/:id"
        element={
          FEATURES.COACHING ? (
            <PageShell headerProps={headerProps}>
              <div className="card-paper p-6">
                <SessionDetail />
              </div>
            </PageShell>
          ) : HIDDEN_ROUTE
        }
      />

      {/* OAuth callback route */}
      <Route path="/auth/callback" element={<OAuthCallback />} />
    </Routes>
    </>
  );
}

function AppRouter() {
  const { user, isAuthenticated, isLoading, checkSession, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [member, setMember] = useState(null);
  const [memberError, setMemberError] = useState(null);
  const [memberLoading, setMemberLoading] = useState(false);

  // Fetch member info when authenticated (once)
  // memberFetchedRef prevents re-fetching; memberLoading gates rendering until resolved
  const memberFetchedRef = React.useRef(false);
  useEffect(() => {
    if (isAuthenticated && !memberFetchedRef.current) {
      memberFetchedRef.current = true;
      setMemberLoading(true);
      getCurrentMember()
        .then((res) => {
          if (res?.member) setMember(res.member);
          setMemberError(null);
          const checkinRedirect = sessionStorage.getItem('larynx_checkin_redirect');
          if (checkinRedirect) {
            // Always clear the key, even when check-in is hidden — a stale
            // redirect left in sessionStorage would fire on a later sign-in.
            sessionStorage.removeItem('larynx_checkin_redirect');
            if (FEATURES.GAMIFICATION) window.location.replace(checkinRedirect);
            return;
          }

          // JoinPage stashes the invite token before bouncing to sign-in. Without
          // reading it back here the invitee lands on the dashboard and has to
          // dig the emailed link out again. JoinPage clears the key on success.
          const inviteToken = sessionStorage.getItem('inviteToken');
          if (inviteToken) {
            window.location.hash = `#/join?token=${inviteToken}`;
          }
        })
        .catch((err) => {
          console.warn('[App] Could not fetch member:', err);
          setMemberError(err.code || 'UNKNOWN');
        })
        .finally(() => setMemberLoading(false));
    }
  }, [isAuthenticated]);

  const handleLoginClick = () => {
    setIsAuthModalOpen(true);
  };

  const handleAuthSuccess = () => {
    checkSession();
    setMember(null);
    setMemberError(null);
    setMemberLoading(false);
    memberFetchedRef.current = false;
  };

  const handleLogout = () => {
    setMember(null);
    setMemberError(null);
    setMemberLoading(false);
    memberFetchedRef.current = false;
    logout();
  };

  const headerProps = {
    user,
    isAuthenticated,
    member,
    onLoginClick: handleLoginClick,
    onLogoutClick: handleLogout,
    theme,
    toggleTheme,
  };

  // Loading state with Larry (auth session check)
  if (isLoading) {
    return (
      <div className="min-h-screen bg-canvas flex flex-col items-center justify-center gap-4">
        <Larry mood="thinking" size={80} message="One moment." />
      </div>
    );
  }

  // Block render while member status fetch is in-flight — prevents Dashboard from
  // firing API calls before we know if the user is a member/pending/not-a-member
  if (isAuthenticated && memberLoading) {
    return (
      <div className="min-h-screen bg-canvas flex flex-col items-center justify-center gap-4">
        <Larry mood="thinking" size={80} message="Checking your account…" />
      </div>
    );
  }

  // Join/checkin routes are always accessible (even unauthenticated or pending)
  const isJoinRoute = window.location.hash.startsWith('#/join');
  const isCheckinRoute = FEATURES.GAMIFICATION && window.location.hash.startsWith('#/checkin');
  // Landing page for unauthenticated users
  if (!isAuthenticated) {
    if (isJoinRoute || isCheckinRoute) {
      return (
        <Routes>
          <Route path="/join" element={<JoinPage />} />
          <Route path="/checkin" element={FEATURES.GAMIFICATION ? <CheckinPage /> : HIDDEN_ROUTE} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      );
    }

    return (
      <>
        <LandingPage onSignInClick={handleLoginClick} />
        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
          onSuccess={handleAuthSuccess}
        />
      </>
    );
  }

  // Non-members (no account in DataStore, not auto-approved domain) see the join request screen
  if (memberError === 'NOT_A_MEMBER' && !isJoinRoute) {
    return <JoinRequestScreen user={user} onLogout={handleLogout} />;
  }

  // Pending members can still access /join (to accept their invitation)
  if (memberError === 'MEMBERSHIP_PENDING' && !isJoinRoute) {
    return (
      <div className="min-h-screen bg-canvas flex flex-col items-center justify-center gap-4 px-4">
        <Larry mood="thinking" size={80} />
        <h2 className="text-xl font-extrabold" style={{ color: 'var(--text-primary)' }}>Your request is in.</h2>
        <p className="text-center max-w-md" style={{ color: 'var(--text-secondary)' }}>
          The admin will review it shortly. Have an invitation link? Use it to skip the queue.
        </p>
        <button
          onClick={handleLogout}
          className="mt-4 pill-tab"
          style={{ color: 'var(--text-muted)' }}
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <>
      <ToastContainer />
      <AppContent headerProps={headerProps} />
      <AppFeedbackButton member={member} />
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onSuccess={handleAuthSuccess}
      />
    </>
  );
}

export default function App() {
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('logged_out') === '1') {
      useAuthStore.getState().clearUser();
      localStorage.removeItem('speech-coach-auth');
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, []);

  React.useEffect(() => {
    if (window.location.pathname.endsWith('/index.html')) {
      window.location.replace(window.location.pathname.replace('/index.html', '/'));
    }
  }, []);

  return (
    <HashRouter>
      <AppRouter />
    </HashRouter>
  );
}

